const DECOR_ATTR = 'data-mc-export-decor';
const MAX_GALLERY_GUTTER_PX = 24;

const HIDE_PSEUDO_CSS = `
.mc-invite .mc-copy::before,
.mc-invite .mc-copy::after,
.mc-invite .mc-gallery::before,
.mc-invite .mc-gallery::after,
.mc-invite figure.mc-photo::after {
  content: none !important;
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
}
`;

function pseudoHasBox(pseudo) {
  const content = pseudo.content;
  if (content && content !== 'none' && content !== 'normal') return true;
  const w = parseFloat(pseudo.width);
  const h = parseFloat(pseudo.height);
  return (Number.isFinite(w) && w > 0) || (Number.isFinite(h) && h > 0);
}

function pseudoBackground(pseudoStyle) {
  if (pseudoStyle.backgroundColor && pseudoStyle.backgroundColor !== 'rgba(0, 0, 0, 0)') {
    return pseudoStyle.backgroundColor;
  }
  if (pseudoStyle.background && pseudoStyle.background !== 'none') {
    return pseudoStyle.background;
  }
  return '#F9BE0B';
}

function ensurePositionedHost(host) {
  if (!host) return () => {};
  const prev = host.style.position;
  const computed = getComputedStyle(host).position;
  if (computed === 'static') {
    host.style.position = 'relative';
    return () => {
      host.style.position = prev;
    };
  }
  return () => {};
}

function pseudoDecorDiv(name, pseudoStyle) {
  const div = document.createElement('div');
  div.setAttribute(DECOR_ATTR, name);
  div.style.position = pseudoStyle.position === 'static' ? 'absolute' : pseudoStyle.position;
  div.style.left = pseudoStyle.left;
  div.style.top = pseudoStyle.top;
  div.style.right = pseudoStyle.right;
  div.style.bottom = pseudoStyle.bottom;
  div.style.width = pseudoStyle.width;
  div.style.height = pseudoStyle.height;
  div.style.background = pseudoBackground(pseudoStyle);
  div.style.transform = pseudoStyle.transform;
  div.style.transformOrigin = pseudoStyle.transformOrigin;
  if (pseudoStyle.clipPath && pseudoStyle.clipPath !== 'none') {
    div.style.clipPath = pseudoStyle.clipPath;
    div.style.webkitClipPath = pseudoStyle.clipPath;
  }
  if (pseudoStyle.opacity && pseudoStyle.opacity !== '1') {
    div.style.opacity = pseudoStyle.opacity;
  }
  div.style.pointerEvents = 'none';
  const z = pseudoStyle.zIndex;
  if (z && z !== 'auto') div.style.zIndex = z;
  return div;
}

function pseudoHasVisibleDecor(pseudoStyle) {
  if (pseudoHasBox(pseudoStyle)) return true;
  const clip = pseudoStyle.clipPath;
  return Boolean(clip && clip !== 'none');
}

function parseCssLengthPx(value, parentPx, fallbackPx) {
  const raw = String(value || '').trim();
  if (!raw || raw === 'auto') return fallbackPx;
  if (raw.endsWith('%')) {
    const pct = parseFloat(raw);
    return Number.isFinite(pct) && parentPx > 0 ? (parentPx * pct) / 100 : fallbackPx;
  }
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : fallbackPx;
}

function effectiveClipPath(pseudoStyle) {
  const clip = pseudoStyle.clipPath || pseudoStyle.webkitClipPath;
  return clip && clip !== 'none' ? clip : '';
}

/** html2canvas ignores clip-path — draw Coopers-style angled wedge as SVG. */
function copyAngledDecorSvg(copyEl, afterStyle) {
  const parentW = copyEl.offsetWidth || copyEl.clientWidth || 1;
  const h = Math.max(1, copyEl.offsetHeight || copyEl.clientHeight || 1);
  const w = Math.max(
    1,
    Math.round(parseCssLengthPx(afterStyle.width, parentW, Math.min(180, parentW * 0.28))),
  );
  const leftPct = 0.48;
  const rightPct = 0.52;
  const points = `0,0 ${w * leftPct},0 ${w},${h} ${w * rightPct},${h}`;

  const wrap = document.createElement('div');
  wrap.setAttribute(DECOR_ATTR, 'copy-angled');
  wrap.style.position = afterStyle.position === 'static' ? 'absolute' : afterStyle.position;
  wrap.style.left = afterStyle.left;
  wrap.style.right = afterStyle.right;
  wrap.style.top = afterStyle.top;
  wrap.style.bottom = afterStyle.bottom;
  wrap.style.width = afterStyle.width;
  wrap.style.height = afterStyle.height;
  wrap.style.pointerEvents = 'none';
  wrap.style.zIndex = afterStyle.zIndex && afterStyle.zIndex !== 'auto' ? afterStyle.zIndex : '1';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.style.display = 'block';

  const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
  poly.setAttribute('points', points);
  poly.setAttribute('fill', pseudoBackground(afterStyle));
  const op = parseFloat(afterStyle.opacity);
  if (Number.isFinite(op) && op < 1) poly.setAttribute('opacity', String(op));
  svg.appendChild(poly);
  wrap.appendChild(svg);
  return wrap;
}

function copyAngledDecor(copyEl, afterStyle) {
  const clip = effectiveClipPath(afterStyle);
  if (clip && /polygon/i.test(clip)) {
    return copyAngledDecorSvg(copyEl, afterStyle);
  }
  if (clip) {
    const div = pseudoDecorDiv('copy-angled', afterStyle);
    div.style.clipPath = clip;
    div.style.webkitClipPath = clip;
    return div;
  }
  return null;
}

/** @param {object} styleLike getComputedStyle(gallery) or test stub */
export function parseGalleryGutterWidthPx(galleryEl, styleLike) {
  const style = styleLike || getComputedStyle(galleryEl);
  const sources = [
    galleryEl.style.background,
    galleryEl.style.backgroundImage,
    style.backgroundImage,
    style.background,
  ].filter(Boolean);
  for (const raw of sources) {
    const bg = String(raw);
    if (!bg || bg === 'none') continue;
    if (!bg.includes('gradient')) continue;
    const m = bg.match(/linear-gradient\([^)]+\)/i);
    if (!m) continue;
    const grad = m[0];
    if (!/90deg|to right/i.test(grad)) continue;
    const pxStops = [...grad.matchAll(/(\d+(?:\.\d+)?)px/g)]
      .map((x) => parseFloat(x[1]))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (pxStops.length) {
      const narrow = pxStops.filter((n) => n <= MAX_GALLERY_GUTTER_PX);
      const w = narrow.length ? Math.min(...narrow) : Math.min(...pxStops);
      return Math.max(1, Math.min(MAX_GALLERY_GUTTER_PX, Math.round(w)));
    }
    return 10;
  }
  return 0;
}

function galleryGutterWidth(galleryEl) {
  return parseGalleryGutterWidthPx(galleryEl);
}

/** Skip legacy gold gutter when gallery uses solid dark fill (Coopers uses ::before frame, not stripe bg). */
function galleryUsesStripeGutter(galleryEl) {
  const style = getComputedStyle(galleryEl);
  const gutterW = parseGalleryGutterWidthPx(galleryEl, style);
  if (gutterW <= 0) return false;
  const bgImg = style.backgroundImage || '';
  if (bgImg && bgImg !== 'none' && /linear-gradient/i.test(bgImg) && /90deg|to right/i.test(bgImg)) {
    return true;
  }
  const bg = (style.backgroundColor || '').replace(/\s/g, '').toLowerCase();
  if (bg && bg !== 'rgba(0,0,0,0)' && bg !== 'transparent') {
    return false;
  }
  return gutterW > 0;
}

function photoOverlayFromPseudo(afterStyle) {
  const probe = afterStyle.backgroundImage || afterStyle.background || '';
  if (!probe || probe === 'none') return null;
  if (!String(probe).includes('gradient')) return null;

  const div = document.createElement('div');
  div.setAttribute(DECOR_ATTR, 'photo-overlay');
  div.style.position = 'absolute';
  div.style.inset = '0';
  const painted = afterStyle.background;
  if (painted && painted !== 'none') {
    div.style.background = painted;
  } else if (afterStyle.backgroundImage && afterStyle.backgroundImage !== 'none') {
    div.style.background = afterStyle.backgroundImage;
  } else {
    return null;
  }
  div.style.pointerEvents = 'none';
  div.style.zIndex = '1';
  return div;
}

/**
 * Replace ::before/::after decorations html2canvas cannot paint with real DOM nodes.
 * @returns {() => void}
 */
export function materializeExportDecorations(root) {
  if (!root) return () => {};

  const restores = [];

  const hideStyle = document.createElement('style');
  hideStyle.setAttribute('data-mc-export-decor-hide', '1');
  hideStyle.textContent = HIDE_PSEUDO_CSS;
  root.prepend(hideStyle);
  restores.push({ type: 'hideStyle', node: hideStyle });

  root.querySelectorAll('.mc-copy').forEach((copyEl) => {
    let beforeStyle;
    let afterStyle;
    try {
      beforeStyle = getComputedStyle(copyEl, '::before');
      afterStyle = getComputedStyle(copyEl, '::after');
    } catch {
      return;
    }
    const restoreCopyPos = ensurePositionedHost(copyEl);
    restores.push({ type: 'fn', run: restoreCopyPos });
    if (pseudoHasVisibleDecor(beforeStyle)) {
      const stripe = pseudoDecorDiv('copy-stripe', beforeStyle);
      copyEl.insertBefore(stripe, copyEl.firstChild);
      restores.push({ type: 'node', parent: copyEl, node: stripe });
    }
    if (pseudoHasVisibleDecor(afterStyle)) {
      const angled = copyAngledDecor(copyEl, afterStyle);
      if (angled) {
        copyEl.appendChild(angled);
        restores.push({ type: 'node', parent: copyEl, node: angled });
      }
    }
  });

  root.querySelectorAll('.mc-gallery').forEach((gallery) => {
    let beforeStyle;
    let afterStyle;
    try {
      beforeStyle = getComputedStyle(gallery, '::before');
      afterStyle = getComputedStyle(gallery, '::after');
    } catch {
      beforeStyle = null;
      afterStyle = null;
    }
    const restoreGalleryPos = ensurePositionedHost(gallery);
    restores.push({ type: 'fn', run: restoreGalleryPos });
    if (beforeStyle && pseudoHasVisibleDecor(beforeStyle)) {
      const frame = pseudoDecorDiv('gallery-before', beforeStyle);
      gallery.insertBefore(frame, gallery.firstChild);
      restores.push({ type: 'node', parent: gallery, node: frame });
    }
    if (afterStyle && pseudoHasVisibleDecor(afterStyle)) {
      const tail = pseudoDecorDiv('gallery-after', afterStyle);
      gallery.appendChild(tail);
      restores.push({ type: 'node', parent: gallery, node: tail });
    }

    const style = getComputedStyle(gallery);
    if (!galleryUsesStripeGutter(gallery)) return;
    const gutterW = galleryGutterWidth(gallery);
    if (gutterW <= 0) return;

    const gutter = document.createElement('div');
    gutter.setAttribute(DECOR_ATTR, 'gallery-gutter');
    gutter.style.position = 'absolute';
    gutter.style.left = '0';
    gutter.style.top = '0';
    gutter.style.bottom = '0';
    gutter.style.width = `${gutterW}px`;
    gutter.style.background = '#F9BE0B';
    gutter.style.pointerEvents = 'none';
    gutter.style.zIndex = '2';

    const pos = style.position;
    if (pos === 'static') {
      restores.push({
        type: 'gallery-pos',
        gallery,
        position: gallery.style.position,
      });
      gallery.style.position = 'relative';
    }

    restores.push({
      type: 'gallery-bg',
      gallery,
      background: gallery.style.background,
      backgroundImage: gallery.style.backgroundImage,
    });
    gallery.style.background = 'transparent';
    gallery.style.backgroundImage = 'none';

    gallery.insertBefore(gutter, gallery.firstChild);
    restores.push({ type: 'node', parent: gallery, node: gutter });
  });

  root.querySelectorAll('.mc-photo').forEach((figure) => {
    let afterStyle;
    try {
      afterStyle = getComputedStyle(figure, '::after');
    } catch {
      return;
    }
    const overlay = photoOverlayFromPseudo(afterStyle);
    if (!overlay) return;

    const pos = getComputedStyle(figure).position;
    if (pos === 'static') {
      restores.push({
        type: 'photo-pos',
        figure,
        position: figure.style.position,
      });
      figure.style.position = 'relative';
    }

    figure.appendChild(overlay);
    restores.push({ type: 'node', parent: figure, node: overlay });
  });

  return () => {
    restores.reverse().forEach((entry) => {
      if (entry.type === 'hideStyle') {
        entry.node.remove();
      } else if (entry.type === 'node') {
        entry.node.remove();
      } else if (entry.type === 'gallery-bg') {
        entry.gallery.style.background = entry.background;
        entry.gallery.style.backgroundImage = entry.backgroundImage;
      } else if (entry.type === 'gallery-pos') {
        entry.gallery.style.position = entry.position;
      } else if (entry.type === 'photo-pos') {
        entry.figure.style.position = entry.position;
      } else if (entry.type === 'fn') {
        entry.run?.();
      }
    });
  };
}
